import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { incrementUnread } from "../../redux/chatSlice";
import { selectActiveRooms } from "../../redux/chatSlice";
import { getSocket } from "../../services/chatSocket";

const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.id || payload._id || payload.sub || null;
  } catch {
    return null;
  }
};

const NotificationSocket = () => {
  const dispatch = useDispatch();
  const joinedRoomsRef = useRef(new Set());
  const roomIdsRef = useRef([]);
  const activeRoomsRef = useRef([]);

  const therapistRequests = useSelector(
    (s) => s.therapist?.therapist?.requests,
  );
  const userRequests = useSelector((s) => s.users?.currentUser?.requests);
  const fetchedRequests = useSelector((s) => s.requests?.requests);
  const activeRooms = useSelector(selectActiveRooms);

  const roomIds = [
    ...(Array.isArray(therapistRequests) ? therapistRequests : []),
    ...(Array.isArray(userRequests) ? userRequests : []),
    ...(Array.isArray(fetchedRequests) ? fetchedRequests : []),
  ]
    .map((r) => r._id || r.id)
    .filter(Boolean)
    // Also include active rooms (e.g., from ChatPage when user is viewing a message)
    .concat(activeRooms || [])
    // Remove duplicates
    .filter((id, i, arr) => arr.indexOf(id) === i);

  console.log("[NS] roomIds:", roomIds, "activeRooms:", activeRooms);

  // Always keep ref in sync so the connect handler uses the latest list
  roomIdsRef.current = roomIds;
  activeRoomsRef.current = (activeRooms || []).map((id) => String(id));

  // Initialize socket and attach global listeners once
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const joinAllRooms = () => {
      roomIdsRef.current.forEach((id) => {
        if (!joinedRoomsRef.current.has(id)) {
          socket.emit("join", { requestId: id });
          joinedRoomsRef.current.add(id);
        }
      });
    };

    const onConnect = () => {
      joinedRoomsRef.current.clear();
      joinAllRooms();
    };

    const onDisconnect = () => {
      joinedRoomsRef.current.clear();
    };

    const onMessage = (msg) => {
      const myId = getCurrentUserId();
      const senderId = msg.senderId?._id || msg.senderId;
      const requestId =
        msg.requestId ||
        msg.request?._id ||
        msg.request ||
        msg.roomId ||
        msg.room;

      const normalizedRequestId = requestId ? String(requestId) : null;

      if (senderId && String(senderId) !== String(myId) && normalizedRequestId) {
        // Only skip badge if the user is ACTIVELY VIEWING this exact room
        const isActiveRoom = activeRoomsRef.current.includes(normalizedRequestId);
        if (!isActiveRoom) {
          dispatch(incrementUnread({ requestId: normalizedRequestId }));
        }
      }
    };

    // Join immediately if already connected; otherwise connect handler will join
    if (socket.connected) joinAllRooms();

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("message:new", onMessage);

    // Listen to other possible event names
    socket.on("message", onMessage);
    socket.on("new-message", onMessage);
    socket.on("chat:message", onMessage);
    socket.on("msg", onMessage);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("message:new", onMessage);
      socket.off("message", onMessage);
      socket.off("new-message", onMessage);
      socket.off("chat:message", onMessage);
      socket.off("msg", onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Join any new rooms that appear after the socket is already connected
  useEffect(() => {
    const socket = getSocket();
    if (!socket || roomIds.length === 0) return;

    if (socket.connected) {
      roomIds.forEach((id) => {
        if (!joinedRoomsRef.current.has(id)) {
          socket.emit("join", { requestId: id });
          joinedRoomsRef.current.add(id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomIds.join(",")]);

  return null;
};

export default NotificationSocket;
